import { appConfig, featureConfig } from '@/utils/env.js'

const SENSITIVE_KEY_PATTERN = /(token|password|secret|authorization|cookie|csrf|otp|code)/i

const isConsoleEnabled = () => featureConfig.enableConsoleLogs || featureConfig.enableDebug || appConfig.env === 'development'

const now = () => new Date().toISOString()

const makeRequestId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const sanitizeValue = (value, depth = 0) => {
  if (value == null) return value
  if (depth > 4) return '[MaxDepth]'
  if (value instanceof Error) return serializeError(value)
  if (typeof FormData !== 'undefined' && value instanceof FormData) return '[FormData]'
  if (typeof File !== 'undefined' && value instanceof File) {
    return { name: value.name, size: value.size, type: value.type }
  }
  if (Array.isArray(value)) return value.slice(0, 25).map((item) => sanitizeValue(item, depth + 1))
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SENSITIVE_KEY_PATTERN.test(key) ? '[REDACTED]' : sanitizeValue(item, depth + 1)
      ])
    )
  }
  return value
}

const serializeError = (error) => ({
  name: error?.name,
  message: error?.message,
  stack: appConfig.env === 'production' ? undefined : error?.stack,
  status: error?.response?.status,
  requestId: error?.config?._requestId || error?.response?.headers?.['x-request-id'],
  url: error?.config?.url,
  method: error?.config?.method?.toUpperCase()
})

const write = (level, message, context = {}) => {
  const entry = {
    ts: now(),
    app: appConfig.name,
    env: appConfig.env,
    level,
    message,
    ...sanitizeValue(context)
  }

  if (!isConsoleEnabled() && level !== 'error') return entry

  const method = level === 'error' ? 'error' : level === 'warn' ? 'warn' : level === 'debug' ? 'debug' : 'log'
  return entry
}

const writeAudit = (message, context = {}) => {
  const entry = {
    ts: now(),
    app: appConfig.name,
    env: appConfig.env,
    level: 'audit',
    message,
    ...sanitizeValue(context)
  }

  if (featureConfig.enableSsoAuditLogs) {
    console.info(`[AUDIT] ${message}`, entry)
  }

  return entry
}

export const logger = {
  makeRequestId,
  sanitize: sanitizeValue,
  serializeError,
  audit: (message, context) => writeAudit(message, context),
  debug: (message, context) => write('debug', message, context),
  info: (message, context) => write('info', message, context),
  warn: (message, context) => write('warn', message, context),
  error: (message, context) => write('error', message, context)
}

export const installGlobalErrorLogging = (app) => {
  app.config.errorHandler = (error, instance, info) => {
    logger.error('Vue error', {
      error,
      component: instance?.type?.name || instance?.type?.__name,
      info
    })
  }

  window.addEventListener('error', (event) => {
    logger.error('Browser error', {
      error: event.error || event.message,
      file: event.filename,
      line: event.lineno,
      column: event.colno
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      error: event.reason
    })
  })
}

export default logger
